/**
 * Página de Tareas — vista solo-lectura.
 *
 * Sirve a dos roles que comparten 100% la UX (informar, no controlar) pero
 * difieren en qué tareas reciben del backend y en el copy del header:
 *
 *  - **Cliente (propietario)**: tareas con `verCliente=true` que tocan a
 *    sus propiedades. Default cuando no se pasa `mode`.
 *  - **Aseador**: tareas asignadas a él (`trabajador_id`) + tareas de las
 *    propiedades donde tiene aseo hoy/futuro. El backend ya filtra; en la
 *    UI se etiqueta "Asignada a mí" para distinguir.
 *
 * Diferencias respecto a la vista admin ([tareas.tsx](./tareas.tsx)):
 *  - Sin concepto de "Espacios" ni sectores ni comunas.
 *  - Sin boards ni categorías — se navega por propiedad.
 *  - Sin edición inline, sin botones destructivos.
 *  - Un collapse por propiedad con el resumen de tareas dentro.
 *  - Click en tarea → modal solo-lectura con detalle y galería de imágenes.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  Alert,
  Badge,
  Button,
  Collapse,
  Empty,
  Image,
  Input,
  Modal,
  Select,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CalendarOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  FilterOutlined,
  HomeOutlined,
  PictureOutlined,
  SearchOutlined,
  SortAscendingOutlined,
} from "@ant-design/icons";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import VideoPlayer from "../../Components/VideoPlayer";
import { useSidebar } from "../../hooks/useSidebar";
import { apiFetch } from "../../services/apiClient";
import { downloadFile, downloadFilesAsZip } from "../../utils/downloadHelpers";
import { norm } from "../../utils/fuzzyMatch";
import type { DB, PropertyOption, Task } from "./types";
import { PRIORIDADES_TAREA } from "./types";
import { TareaFotoTile } from "./components/TareaFotoTile";
import "./tareasCliente.css";
// Bridge antd estático: los métodos `Modal.x` / `message.x` /
// `notification.x` importados directo de "antd" no funcionan en
// v5 + React 19 (root React huérfano fuera del ConfigProvider).
// Round 2026-05-01 — sweep general post-fix /sueldos.
import { getMsg } from "../../utils/antdStatic";

const { Paragraph, Title } = Typography;

/** Helper que delega en `apiFetch` y conserva la firma legacy `(path, {json})`. */
async function fetchJson<T>(
  path: string,
  init?: RequestInit & { json?: any },
): Promise<T> {
  const body =
    init?.json !== undefined ? JSON.stringify(init.json) : (init?.body as BodyInit | undefined);
  const data = await apiFetch<T>(path, { ...init, body });
  if (data == null) throw new Error(`Error en ${path}`);
  return data;
}

const PRIORITY_COLOR: Record<string, string> = PRIORIDADES_TAREA.reduce(
  (acc, p) => {
    acc[p.value] = p.color;
    return acc;
  },
  {} as Record<string, string>,
);

const STATUS_COLOR: Record<string, string> = {
  Pendiente: "#fbbf24",
  "En progreso": "#38bdf8",
  Bloqueada: "#ef4444",
  Hecha: "#22c55e",
  Listo: "#22c55e",
};

// Peso para ordenar por prioridad. Mayor = más importante/urgente. Valores
// desconocidos caen al fondo; "postergado" explícitamente va al final
// porque el propietario ya eligió postergarla.
const PRIORITY_WEIGHT: Record<string, number> = {
  alta: 3,
  media: 2,
  baja: 1,
  postergado: 0,
};

type SortKey =
  | "priority-desc"
  | "priority-asc"
  | "date-desc"
  | "date-asc";

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "priority-desc", label: "Prioridad (alta → baja)" },
  { value: "priority-asc", label: "Prioridad (baja → alta)" },
  { value: "date-desc", label: "Más recientes primero" },
  { value: "date-asc", label: "Más antiguas primero" },
];

/** Compara dos tareas según el criterio pedido. Todos los criterios tienen
 * un desempate estable por fecha (desc) e id (asc) para que el orden sea
 * determinístico incluso cuando hay empates en el campo principal. */
function compareTasks(a: Task, b: Task, sort: SortKey): number {
  const wA = PRIORITY_WEIGHT[String(a.prioridad ?? "")] ?? -1;
  const wB = PRIORITY_WEIGHT[String(b.prioridad ?? "")] ?? -1;
  const tA = a.registradoEl ? new Date(a.registradoEl).getTime() : 0;
  const tB = b.registradoEl ? new Date(b.registradoEl).getTime() : 0;

  let primary = 0;
  switch (sort) {
    case "priority-desc":
      primary = wB - wA;
      break;
    case "priority-asc":
      primary = wA - wB;
      break;
    case "date-desc":
      primary = tB - tA;
      break;
    case "date-asc":
      primary = tA - tB;
      break;
  }
  if (primary !== 0) return primary;
  // Desempate: más recientes primero → si siguen empatadas, id ascendente.
  if (tB !== tA) return tB - tA;
  return Number(a.id) - Number(b.id);
}

function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  const parts = iso.slice(0, 10).split("-");
  if (parts.length !== 3) return iso;
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
}

function taskTitle(t: Task): string {
  const d = (t.descripcion ?? "").trim();
  if (d) return d.length > 80 ? `${d.slice(0, 77)}…` : d;
  return "Tarea sin título";
}

type TareasMode = "cliente" | "aseador";

type Copy = {
  title: string;
  help: string;
  emptyNoProps: string;
  emptyNoTasks: string;
  filterAllOptionLabel: string;
  filterTooltip: string;
  countNoun: (n: number) => string;
};

const COPY: Record<TareasMode, Copy> = {
  cliente: {
    title: "Tareas",
    help: "Aquí encuentras las observaciones, mantenciones y tareas pendientes de tus propiedades. Esta vista es informativa — no puedes editarla desde acá.",
    emptyNoProps: "No tienes propiedades asociadas aún. Contacta al administrador si crees que esto es un error.",
    emptyNoTasks: "No hay tareas registradas en tus propiedades. ¡Todo al día!",
    filterAllOptionLabel: "Todas mis propiedades",
    filterTooltip: "Filtrar por propiedad",
    countNoun: (n: number) => (n === 1 ? "tarea" : "tareas"),
  },
  aseador: {
    title: "Tareas",
    // El aseador no es dueño de las propiedades — va a propiedades a
    // resolver cosas. Por eso no decimos "tus propiedades", pero sí
    // mantenemos la palabra "propiedades" (no "deptos", "casas", etc.) —
    // ese es el nombre canónico del recurso en todo el sistema.
    help: "Tareas asignadas a ti más las pendientes de las propiedades donde tienes aseo hoy o a futuro. Sirven para saber qué resolver en cada propiedad donde vas a estar.",
    emptyNoProps: "Sin tareas a la vista. Cuando te asignen una o tengas un aseo agendado, las verás acá.",
    emptyNoTasks: "Sin tareas pendientes en las propiedades de tus aseos. Todo al día.",
    filterAllOptionLabel: "Todas las propiedades",
    filterTooltip: "Filtrar por propiedad",
    countNoun: (n: number) => (n === 1 ? "tarea" : "tareas"),
  },
};

export default function TareasCliente({ mode = "cliente" }: { mode?: TareasMode } = {}) {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const { user } = useAuth();
  const copy = COPY[mode];

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // El backend ya nos devuelve sólo las propiedades del cliente logueado
  // (filtra por `id_cliente = userId` en /tareas/db). El cliente puede ser
  // a la vez "propietario" en términos de negocio, pero el filtro relevante
  // aquí es la relación Property.id_cliente → User. No re-filtramos en
  // frontend para evitar perder propiedades.
  const [misPropiedades, setMisPropiedades] = useState<PropertyOption[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);

  // Filtros
  const [fProp, setFProp] = useState<string | "all">("all");
  const [fQuery, setFQuery] = useState("");
  const [fOnlyOpen, setFOnlyOpen] = useState(false);
  const [fSort, setFSort] = useState<SortKey>("priority-desc");

  // Modal de detalle
  const [detailTask, setDetailTask] = useState<Task | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | string | null>(null);
  const [downloadingAll, setDownloadingAll] = useState(false);

  // Descarga un adjunto de la tarea (imagen o video). Usa el `name` del
  // backend si está, si no infiere del MIME (ver downloadHelpers).
  const downloadOneAttachment = async (
    img: { id: number | string; url: string; name?: string },
  ) => {
    setDownloadingId(img.id);
    try {
      await downloadFile(img.url, img.name);
    } catch (e: any) {
      getMsg()?.error(e?.message || "No se pudo descargar el archivo");
    } finally {
      setDownloadingId(null);
    }
  };

  // Descarga TODOS los adjuntos de la tarea actual (fotos + videos) en
  // un único ZIP. El nombre del ZIP usa el id de la tarea.
  const downloadAllAttachments = async () => {
    if (!detailTask) return;
    const attachments = (detailTask.imagenes ?? []) as Array<{
      id: number | string;
      url: string;
      name?: string;
    }>;
    if (attachments.length === 0) return;
    setDownloadingAll(true);
    try {
      await downloadFilesAsZip(
        attachments.map((a, i) => ({
          url: a.url,
          name: a.name || `tarea${detailTask.id}-${i + 1}`,
        })),
        `tarea${detailTask.id}-archivos`,
      );
      getMsg()?.success(`${attachments.length} archivos empaquetados en ZIP`);
    } catch (e: any) {
      getMsg()?.error(e?.message || "No se pudo armar el ZIP");
    } finally {
      setDownloadingAll(false);
    }
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 1) /tareas/db trae propiedades + spaces visibles al rol actual.
      //    Backend ya filtra propiedades por id_cliente=userId; lo tomamos
      //    como fuente de verdad sin filtrado adicional en frontend.
      const db = await fetchJson<DB>("/tareas/db");
      const props = db.propiedades ?? [];
      setMisPropiedades(props);

      // 2) Por cada space no-system, traer tareas. Backend ya filtra a
      //    permitir_ver_cliente=true + propiedad del cliente. Confiamos.
      const nonSystemSpaces = (db.spaces ?? []).filter((s) => !s.isSystem);
      const results = await Promise.all(
        nonSystemSpaces.map((s) =>
          fetchJson<Task[]>(
            `/tareas?${new URLSearchParams({ spaceId: String(s.id) }).toString()}`,
          ).catch(() => [] as Task[]),
        ),
      );
      setTasks(results.flat());
    } catch (e: any) {
      console.error(e);
      setError(e?.message ?? "Error cargando tareas");
      getMsg()?.error("No pude cargar tus tareas. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // Set de IDs propios para indexar tareas por propiedad (defensa en
  // profundidad — el backend ya filtra, pero esto evita que tareas
  // mal-asociadas se filtren a vistas erradas).
  const userPropIds = useMemo(
    () => new Set(misPropiedades.map((p) => String(p.id))),
    [misPropiedades],
  );

  // Agrupa las tareas filtradas por propiedad (una tarea con N propiedades
  // aparece bajo cada una).
  const tasksByProperty = useMemo(() => {
    // norm() pliega tildes ("reparacion" encuentra "reparación", "bano"
    // encuentra "baño"), como la vista admin del módulo. Aud. 2026-07-13.
    const q = norm(fQuery);
    const map = new Map<string, Task[]>();
    for (const p of misPropiedades) map.set(String(p.id), []);

    for (const t of tasks) {
      if (fOnlyOpen) {
        // Estados TERMINALES reales del ENUM/UI: "finalizado" (actual) +
        // "resuelto"/"cancelado" (legacy). Antes comparaba contra "Hecha" y
        // "Listo", valores que no existen en los datos → el toggle nunca
        // ocultaba nada (auditoría 2026-07-13).
        const estado = (t.estado ?? "").toString();
        if (
          estado === "finalizado" ||
          estado === "resuelto" ||
          estado === "cancelado"
        )
          continue;
      }
      if (q) {
        const hay = norm(t.descripcion ?? "");
        if (!hay.includes(q)) continue;
      }
      const ids = Array.isArray(t.propiedadIds)
        ? t.propiedadIds.map((x: any) => String(x))
        : [];
      for (const pid of ids) {
        if (!userPropIds.has(pid)) continue;
        if (fProp !== "all" && pid !== fProp) continue;
        if (!map.has(pid)) map.set(pid, []);
        map.get(pid)!.push(t);
      }
    }
    // Orden dentro de cada grupo según el criterio elegido por el cliente.
    map.forEach((arr) => arr.sort((a, b) => compareTasks(a, b, fSort)));
    return map;
  }, [tasks, misPropiedades, userPropIds, fProp, fQuery, fOnlyOpen, fSort]);

  const totalTasks = useMemo(() => {
    let c = 0;
    tasksByProperty.forEach((arr) => (c += arr.length));
    return c;
  }, [tasksByProperty]);

  if (loading) {
    return (
      <Layout sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar}>
        <div className="tc-loading">
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar}>
        <div className="tc-page">
          <Alert
            type="error"
            message="No pudimos cargar tus tareas"
            description={error}
            showIcon
            action={
              <Button size="small" onClick={() => void loadAll()}>
                Reintentar
              </Button>
            }
          />
        </div>
      </Layout>
    );
  }

  if (misPropiedades.length === 0) {
    return (
      <Layout sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar}>
        <div className="tc-page">
          <PageHeader title={copy.title} />
          <Empty description={copy.emptyNoProps} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar}>
      <div className="tc-page">
        <PageHeader
          title={copy.title}
          helpText={copy.help}
        />

        {/* Toolbar de filtros */}
        <div className="tc-toolbar">
          <Tooltip title={copy.filterTooltip}>
            <Select
              className="tc-filter"
              value={fProp}
              onChange={(v) => setFProp(v)}
              style={{ minWidth: 240 }}
              suffixIcon={<HomeOutlined />}
              options={[
                { value: "all", label: copy.filterAllOptionLabel },
                ...misPropiedades.map((p) => ({
                  value: String(p.id),
                  label: p.name,
                })),
              ]}
            />
          </Tooltip>
          <Input
            className="tc-search"
            placeholder="Buscar en tareas..."
            allowClear
            prefix={<SearchOutlined />}
            value={fQuery}
            onChange={(e) => setFQuery(e.target.value)}
          />
          <Tooltip title="Ocultar tareas ya terminadas">
            <Button
              type={fOnlyOpen ? "primary" : "default"}
              icon={<FilterOutlined />}
              onClick={() => setFOnlyOpen((v) => !v)}
            >
              {fOnlyOpen ? "Sólo pendientes" : "Ver todas"}
            </Button>
          </Tooltip>
          <Tooltip title="Ordenar tareas">
            <Select
              className="tc-filter"
              value={fSort}
              onChange={(v) => setFSort(v)}
              style={{ minWidth: 220 }}
              suffixIcon={<SortAscendingOutlined />}
              options={SORT_OPTIONS}
            />
          </Tooltip>
          <span className="tc-count">
            <Badge color="#7c3aed" count={totalTasks} showZero overflowCount={999} />
            <span className="tc-count-label">
              {totalTasks === 1 ? "tarea" : "tareas"}
            </span>
          </span>
        </div>

        {totalTasks === 0 ? (
          <Empty
            style={{ marginTop: 24 }}
            description={
              fQuery || fProp !== "all" || fOnlyOpen
                ? "No hay tareas que coincidan con el filtro."
                : copy.emptyNoTasks
            }
          />
        ) : (
          <Collapse
            className="tc-props"
            defaultActiveKey={misPropiedades.slice(0, 3).map((p) => String(p.id))}
            expandIconPosition="end"
            items={misPropiedades
              .filter((p) => fProp === "all" || String(p.id) === fProp)
              .map((p) => {
                const items = tasksByProperty.get(String(p.id)) ?? [];
                return {
                  key: String(p.id),
                  label: (
                    <div className="tc-prop-header">
                      <span
                        className="tc-prop-dot"
                        style={{ background: p.color ?? "#7c3aed" }}
                      />
                      <span className="tc-prop-name">{p.name}</span>
                      <Tag color="blue-inverse" style={{ marginLeft: "auto" }}>
                        {items.length} {items.length === 1 ? "tarea" : "tareas"}
                      </Tag>
                    </div>
                  ),
                  children:
                    items.length === 0 ? (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Sin tareas pendientes"
                      />
                    ) : (
                      <ul className="tc-task-list">
                        {items.map((t) => (
                          <li
                            key={t.id}
                            className="tc-task-item"
                            onClick={() => setDetailTask(t)}
                          >
                            <div className="tc-task-main">
                              <span className="tc-task-title">
                                {taskTitle(t)}
                              </span>
                              <div className="tc-task-meta">
                                {mode === "aseador" && user?.id != null && Number(t.trabajadorId) === Number(user.id) ? (
                                  <Tag color="purple" style={{ margin: 0 }}>
                                    Asignada a mí
                                  </Tag>
                                ) : null}
                                {t.prioridad ? (
                                  <Tag
                                    color={
                                      PRIORITY_COLOR[String(t.prioridad)] ?? undefined
                                    }
                                    style={{ margin: 0 }}
                                  >
                                    {String(t.prioridad).charAt(0).toUpperCase() +
                                      String(t.prioridad).slice(1)}
                                  </Tag>
                                ) : null}
                                {t.estado ? (
                                  <Tag
                                    style={{
                                      margin: 0,
                                      background: `${STATUS_COLOR[String(t.estado)] ?? "#e5e7eb"}22`,
                                      borderColor: `${STATUS_COLOR[String(t.estado)] ?? "#e5e7eb"}`,
                                      color:
                                        STATUS_COLOR[String(t.estado)] ?? "#374151",
                                    }}
                                  >
                                    {String(t.estado)}
                                  </Tag>
                                ) : null}
                                {t.registradoEl ? (
                                  <span className="tc-task-date">
                                    <CalendarOutlined /> {fmtDate(t.registradoEl)}
                                  </span>
                                ) : null}
                                {t.costo != null ? (
                                  <span className="tc-task-cost">
                                    ${Number(t.costo).toLocaleString("es-CL")}
                                  </span>
                                ) : null}
                                {t.imagenes && t.imagenes.length > 0 ? (
                                  <span className="tc-task-pics">
                                    <PictureOutlined /> {t.imagenes.length}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ),
                };
              })}
          />
        )}

        <Modal
          open={!!detailTask}
          title={
            <span style={{ display: "inline-flex", alignItems: "center" }}>
              <CheckSquareOutlined style={{ color: "#7c3aed", marginRight: 8 }} />
              Detalle de la tarea
            </span>
          }
          onCancel={() => setDetailTask(null)}
          footer={<Button onClick={() => setDetailTask(null)}>Cerrar</Button>}
          width={640}
        >
          {detailTask ? (
            <div className="tc-detail">
              <Title level={5} style={{ marginTop: 0 }}>
                {taskTitle(detailTask)}
              </Title>

              <div className="tc-detail-tags">
                {detailTask.prioridad ? (
                  <Tag
                    color={PRIORITY_COLOR[String(detailTask.prioridad)] ?? undefined}
                  >
                    Prioridad {String(detailTask.prioridad)}
                  </Tag>
                ) : null}
                {detailTask.estado ? (
                  <Tag
                    style={{
                      background: `${STATUS_COLOR[String(detailTask.estado)] ?? "#e5e7eb"}22`,
                      borderColor: `${STATUS_COLOR[String(detailTask.estado)] ?? "#e5e7eb"}`,
                      color:
                        STATUS_COLOR[String(detailTask.estado)] ?? "#374151",
                    }}
                  >
                    {String(detailTask.estado)}
                  </Tag>
                ) : null}
              </div>

              <dl className="tc-detail-grid">
                <dt>
                  <CalendarOutlined /> Registrada
                </dt>
                <dd>{fmtDate(detailTask.registradoEl)}</dd>
                {detailTask.terminadoEl ? (
                  <>
                    <dt>
                      <ClockCircleOutlined /> Terminada
                    </dt>
                    <dd>{fmtDate(detailTask.terminadoEl)}</dd>
                  </>
                ) : null}
                <dt>
                  <HomeOutlined /> Propiedades
                </dt>
                <dd>
                  {(detailTask.propiedadIds ?? [])
                    .map((pid: any) => {
                      const p = misPropiedades.find(
                        (x) => String(x.id) === String(pid),
                      );
                      return p?.name ?? `#${pid}`;
                    })
                    .join(", ") || "—"}
                </dd>
                {detailTask.costo != null ? (
                  <>
                    <dt>Costo</dt>
                    <dd>${Number(detailTask.costo).toLocaleString("es-CL")}</dd>
                  </>
                ) : null}
              </dl>

              {detailTask.descripcion ? (
                <>
                  <div className="tc-detail-label">Descripción</div>
                  <Paragraph className="tc-detail-desc">
                    {detailTask.descripcion}
                  </Paragraph>
                </>
              ) : null}

              {(() => {
                const adjuntos = detailTask.imagenes ?? [];
                const fotos = adjuntos.filter((a) => a.kind !== "video");
                const videos = adjuntos.filter((a) => a.kind === "video");
                if (adjuntos.length === 0) return null;
                return (
                  <>
                    {/* Botón global "Descargar todas" — visible si hay >=1 adjunto */}
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        loading={downloadingAll}
                        onClick={downloadAllAttachments}
                      >
                        Descargar todas (.zip)
                      </Button>
                    </div>

                    {fotos.length > 0 ? (
                      <>
                        <div className="tc-detail-label">
                          <PictureOutlined /> Imágenes ({fotos.length})
                        </div>
                        <Paragraph
                          className="tc-gallery-hint"
                          type="secondary"
                        >
                          Toca una imagen para abrir la galería y navegar con
                          las flechas. Usa el botón ↓ para descargar.
                        </Paragraph>
                        <Image.PreviewGroup
                          preview={{
                            countRender: (current, total) =>
                              `${current} / ${total}`,
                          }}
                        >
                          <div className="tc-detail-gallery">
                            {fotos.map((img) => (
                              <TareaFotoTile
                                key={img.id}
                                img={img}
                                descargando={downloadingId === img.id}
                                onDescargar={() => downloadOneAttachment(img)}
                              />
                            ))}
                          </div>
                        </Image.PreviewGroup>
                      </>
                    ) : null}

                    {videos.length > 0 ? (
                      <>
                        <div
                          className="tc-detail-label"
                          style={{ marginTop: 14 }}
                        >
                          <PictureOutlined /> Videos ({videos.length})
                        </div>
                        <div className="tc-detail-videos">
                          {videos.map((v) => {
                            const comentario = (v as any)?.comentario as
                              | string
                              | null
                              | undefined;
                            return (
                              <div key={v.id} style={{ position: "relative" }}>
                                <VideoPlayer
                                  className="tc-video-item"
                                  src={v.url}
                                  previewSrc={v.previewUrl}
                                  preload="metadata"
                                  altoMaximo="260px"
                                  // Esta pantalla ya trae su propio botón «Descargar» sobre cada video.
                                  descargable={false}
                                />
                                <Button
                                  size="small"
                                  icon={<DownloadOutlined />}
                                  loading={downloadingId === v.id}
                                  onClick={() => downloadOneAttachment(v)}
                                  style={{
                                    position: "absolute",
                                    top: 6,
                                    right: 6,
                                    zIndex: 5,
                                    background: "rgba(255,255,255,0.85)",
                                  }}
                                >
                                  Descargar
                                </Button>
                                {comentario ? (
                                  <div
                                    style={{
                                      marginTop: 6,
                                      padding: "8px 10px",
                                      background: "#fafafa",
                                      borderRadius: 6,
                                      border: "1px solid #eee",
                                      fontSize: 12.5,
                                      whiteSpace: "pre-wrap",
                                      color: "#444",
                                    }}
                                  >
                                    {comentario}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : null}
                  </>
                );
              })()}
            </div>
          ) : null}
        </Modal>
      </div>
    </Layout>
  );
}

